import { OAuthConfig } from "src/shared/config";
import ApiUtils from "src/shared/services/apiUtils";

const POPUP_WIDTH = 400;
const POPUP_HEIGHT = 426;
const ACCESS_TOKEN_SWIGGY_AZURE_EVENT = "ACCESS_TOKEN_SWIGGY_AZURE_EVENT";

// https://stackoverflow.com/a/63336562
function dec2hex(dec) {
  return ("0" + dec.toString(16)).substr(-2);
}
function generateCodeVerifier() {
  const array = new Uint32Array(56 / 2);
  window.crypto.getRandomValues(array);
  return Array.from(array, dec2hex).join("");
}

function sha256(plain: string) {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return window.crypto.subtle.digest("SHA-256", data);
}

function base64urlencode(a: ArrayBuffer) {
  let str = "";
  const bytes = new Uint8Array(a);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    str += String.fromCharCode(bytes[i]);
  }
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function generateCodeChallenge() {
  const hashed = await sha256(generateCodeVerifier());
  const base64encoded = base64urlencode(hashed);
  return base64encoded;
}

export class OAuthService {
  private config = OAuthConfig;

  async makeAuthUrl() {
    return generateCodeChallenge().then((codeChallenge) => {
      const queryParams = {
        client_id: this.config.CLIENT_ID,
        redirect_uri: `${window.location.origin}${this.config.OAUTH_REDIRECT_URI}`,
        response_type: "token id_token",
        scope: "openid email profile",
        code_challenge: codeChallenge,
        code_challenge_method: "S256",
        nonce: Math.random().toString(36).substring(7),
      };
      return ApiUtils.buildUrlWithParams(`${this.config.OAUTH_LOGIN_ENPOINT}/authorize`, queryParams);
    });
  }

  public launchPopup() {
    const left = window.screen.width / 2 - POPUP_WIDTH / 2,
      top = window.screen.height / 2 - POPUP_HEIGHT / 2;

    this.makeAuthUrl().then((authUrl) => {
      const win = window.open(
        authUrl,
        "Swiggy Login",
        `menubar=no,location=no,resizable=no,scrollbars=no,status=no, width=
          ${POPUP_WIDTH}, height=${POPUP_HEIGHT}, top=${top}, left=${left}`
      );
      if (win) win.opener = window;
    });
  }

  public redirectToLogin() {
    this.makeAuthUrl().then((authUrl) => {
      window.location.href = authUrl;
    });
  }

  public listenToMessageEvent(resolve, reject) {
    const windowEventHandler = (event: MessageEvent) => {
      let hash = event.data;
      // eslint-disable-next-line no-console
      console.log(hash);
      if (hash.type === ACCESS_TOKEN_SWIGGY_AZURE_EVENT) {
        const token = hash.access_token;
        resolve(token);
      } else if (hash.type == "error") {
        console.error(hash.message);
        reject(hash.message);
      }
      window.removeEventListener("message", windowEventHandler);
    };
    window.addEventListener("message", windowEventHandler, false);
  }

  public findTokenInHash(hash: string): string | null {
    const matchedResult = hash.match(/access_token=([^&]+)/);
    return matchedResult && matchedResult[1];
  }

  public processToken(token, callbackFn: (token: string) => void) {
    if (!window.opener) {
      token && callbackFn(token);
      return;
    }
    if (!token) {
      window.opener.postMessage(
        {
          type: "error",
          message: "No Access Token Found.",
        },
        window.location.origin
      );
      window.close();
      return;
    }

    window.opener.postMessage(
      {
        type: ACCESS_TOKEN_SWIGGY_AZURE_EVENT,
        access_token: token,
      },
      window.location.origin
    );
    window.close();
  }
}

export default new OAuthService();
